import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Loader2,
  Check,
  ChevronLeft,
  ChevronRight,
  ChevronsUpDown,
  Search,
  User,
} from "lucide-react";
import * as prettier from "prettier/standalone";
import * as prettierPluginHtml from "prettier/plugins/html";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  RecipientsFilter,
  type FilterItem,
  type FilterProductOption,
} from "@/routes/MessagesSMSCompose/RecipientsFilter";
import { apiFilterToFilterItem, DEFAULT_FILTERS_OPERATOR, filterItemToApiFilter, type FiltersOperator } from "@/lib/contactFilterApi";
import { cn } from "@/lib/utils";
import { PageHtmlChatPanel } from "@/routes/pages/edit/PageHtmlChatPanel";
import { DEFAULT_EMAIL_CAMPAIGN_HTML } from "@/lib/defaultEmailCampaignHtml";
import { EmailCodeEditor } from "./EmailCodeEditor";
import { EmailLexicalEditor } from "./EmailLexicalEditor";

const DEFAULT_FILTERS: FilterItem[] = [];

const STEPS = [
  { number: 1 as const, label: "Content" },
  { number: 2 as const, label: "Recipients" },
  { number: 3 as const, label: "Review" },
];

type ComposeStep = 1 | 2 | 3;

function recipientSummaryText({
  isLoadingRecipients,
  recipientCount,
  leads,
  selectedLeadIds,
}: {
  isLoadingRecipients: boolean;
  recipientCount: number | null;
  leads: { _id: string }[];
  selectedLeadIds: Set<string>;
}) {
  if (isLoadingRecipients) {
    return "Calculating recipients...";
  }
  if (recipientCount === null) {
    return "Recipients: 0";
  }
  if (leads.length > 0) {
    if (selectedLeadIds.size !== recipientCount) {
      return `Recipients: ${selectedLeadIds.size.toLocaleString()} of ${recipientCount.toLocaleString()} selected`;
    }
    return `Recipients: ${selectedLeadIds.size.toLocaleString()}`;
  }
  return `Recipients: ${recipientCount.toLocaleString()}`;
}

export function EmailCampaignCompose() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [step, setStep] = useState<ComposeStep>(1);
  const [subject, setSubject] = useState<string>("");
  const subjectRef = useRef<HTMLDivElement>(null);
  const [html, setHtml] = useState<string>("");
  const htmlRef = useRef(html);
  const [contentTab, setContentTab] = useState("editor");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [testEmail, setTestEmail] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isConfirmSendOpen, setIsConfirmSendOpen] = useState(false);
  const [sendAtLocal, setSendAtLocal] = useState("");

  const [filters, setFilters] = useState<FilterItem[]>(DEFAULT_FILTERS);
  const [filtersOperator, setFiltersOperator] = useState<FiltersOperator>(DEFAULT_FILTERS_OPERATOR);
  const [pages, setPages] = useState<
    { _id: string; title: string; route: string }[]
  >([]);
  const [groups, setGroups] = useState<
    { _id: string; name: string; contacts?: number }[]
  >([]);
  const [products, setProducts] = useState<FilterProductOption[]>([]);
  const [recipientCount, setRecipientCount] = useState<number | null>(null);
  const [leads, setLeads] = useState<
    { _id: string; firstName?: string; lastName?: string; phoneNumber?: string; email?: string }[]
  >([]);
  const [selectedLeadIds, setSelectedLeadIds] = useState<Set<string>>(
    new Set()
  );
  const [isLoadingRecipients, setIsLoadingRecipients] = useState(false);
  const [recipientQueryReady, setRecipientQueryReady] = useState(false);
  const loadedSelectedLeadIdsRef = useRef<string[] | null>(null);
  const recipientFiltersKeyRef = useRef<string>("");

  const [previewRecipientId, setPreviewRecipientId] = useState<string | null>(null);
  const [previewData, setPreviewData] = useState<{
    subject: string;
    html: string;
    to: string;
  } | null>(null);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [recipientSearchQuery, setRecipientSearchQuery] = useState("");
  const [isRecipientPopoverOpen, setIsRecipientPopoverOpen] = useState(false);

  const selectedRecipients = useMemo(() => {
    return leads.filter((l) => selectedLeadIds.has(l._id) && Boolean(l.email?.trim()));
  }, [leads, selectedLeadIds]);

  useEffect(() => {
    if (step === 3 && selectedRecipients.length > 0) {
      if (!previewRecipientId || !selectedRecipients.some((r) => r._id === previewRecipientId)) {
        setPreviewRecipientId(selectedRecipients[0]._id);
      }
    }
  }, [step, selectedRecipients, previewRecipientId]);

  useEffect(() => {
    if (step !== 3 || !previewRecipientId) {
      setPreviewData(null);
      return;
    }

    const currentLead = selectedRecipients.find((r) => r._id === previewRecipientId);
    if (!currentLead) return;

    let isCancelled = false;
    setIsPreviewLoading(true);
    setPreviewError(null);

    const apiFilters = filters
      .map(filterItemToApiFilter)
      .filter((f): f is Record<string, unknown> => f != null);

    fetch("/api/email-campaigns/preview", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        html: htmlRef.current || html,
        subject,
        leadId: currentLead._id,
        email: currentLead.email,
        filters: apiFilters,
      }),
    })
      .then(async (res) => {
        if (!res.ok) {
          const errData = await res.json().catch(() => ({}));
          throw new Error(errData.error || `HTTP ${res.status}`);
        }
        return res.json();
      })
      .then((data) => {
        if (!isCancelled) {
          if (data.preview) {
            setPreviewData(data.preview);
          } else {
            setPreviewError("No preview data received");
          }
        }
      })
      .catch((err) => {
        if (!isCancelled) {
          console.error("Failed to fetch email preview:", err);
          setPreviewError(err.message || "Failed to load preview");
        }
      })
      .finally(() => {
        if (!isCancelled) {
          setIsPreviewLoading(false);
        }
      });

    return () => {
      isCancelled = true;
    };
  }, [step, previewRecipientId, subject, html, filters, selectedRecipients]);

  const filteredPreviewRecipients = useMemo(() => {
    if (!recipientSearchQuery.trim()) return selectedRecipients;
    const q = recipientSearchQuery.toLowerCase();
    return selectedRecipients.filter((r) => {
      const name = [r.firstName, r.lastName].filter(Boolean).join(" ").toLowerCase();
      const email = (r.email || "").toLowerCase();
      return name.includes(q) || email.includes(q);
    });
  }, [selectedRecipients, recipientSearchQuery]);

  const currentRecipientIndex = selectedRecipients.findIndex((r) => r._id === previewRecipientId);
  const currentPreviewLead = selectedRecipients[currentRecipientIndex];

  const handlePrevRecipient = () => {
    if (currentRecipientIndex > 0) {
      setPreviewRecipientId(selectedRecipients[currentRecipientIndex - 1]._id);
    }
  };

  const handleNextRecipient = () => {
    if (currentRecipientIndex < selectedRecipients.length - 1) {
      setPreviewRecipientId(selectedRecipients[currentRecipientIndex + 1]._id);
    }
  };

  const handleSelectLead = useCallback((leadId: string) => {
    setSelectedLeadIds((prev) => {
      const next = new Set(prev);
      if (next.has(leadId)) next.delete(leadId);
      else next.add(leadId);
      return next;
    });
  }, []);

  const handleSelectAll = useCallback(() => {
    if (leads.length === 0) return;
    const allSelected = leads.every((l) => selectedLeadIds.has(l._id));
    setSelectedLeadIds(
      allSelected ? new Set() : new Set(leads.map((l) => l._id))
    );
  }, [leads, selectedLeadIds]);

  const hasPageFilter = filters.some((f) => f.filterType === "page");
  const hasGroupFilter = filters.some((f) => f.filterType === "contactGroup");
  const hasProductFilter = filters.some((f) => f.filterType === "product");
  useEffect(() => {
    if (hasPageFilter) {
      fetch("/api/pages")
        .then((r) => r.json())
        .then((data) => setPages(data.pages || []))
        .catch(() => setPages([]));
    }
  }, [hasPageFilter]);
  useEffect(() => {
    if (hasGroupFilter) {
      fetch("/api/lead-groups")
        .then((r) => r.json())
        .then((data) => setGroups(data.groups || []))
        .catch(() => setGroups([]));
    }
  }, [hasGroupFilter]);
  useEffect(() => {
    if (hasProductFilter) {
      fetch("/api/products")
        .then((r) => r.json())
        .then((data) => setProducts(data.products || []))
        .catch(() => setProducts([]));
    }
  }, [hasProductFilter]);

  useEffect(() => {
    if (step < 2 || !recipientQueryReady) {
      return;
    }

    const fetchRecipientCount = async () => {
      const apiFilters = filters
        .map(filterItemToApiFilter)
        .filter((f): f is Record<string, unknown> => f != null);

      setIsLoadingRecipients(true);
      try {
        const filtersParam = encodeURIComponent(JSON.stringify(apiFilters));
        const operatorParam =
          filtersOperator !== "and" ? `&filtersOperator=${filtersOperator}` : "";
        const response = await fetch(
          `/api/contacts/recipients?filters=${filtersParam}&list=true&channel=email${operatorParam}`
        );
        if (response.ok) {
          const data = await response.json();
          const leadList = data.leads || [];
          const filtersKey = JSON.stringify({ apiFilters, filtersOperator });
          const filtersChanged = filtersKey !== recipientFiltersKeyRef.current;
          recipientFiltersKeyRef.current = filtersKey;

          setRecipientCount(data.count);
          setLeads(leadList);
          if (leadList.length > 0) {
            if (loadedSelectedLeadIdsRef.current) {
              const validIds = new Set(leadList.map((l: { _id: string }) => l._id));
              setSelectedLeadIds(
                new Set(
                  loadedSelectedLeadIdsRef.current.filter((lid) => validIds.has(lid))
                )
              );
              loadedSelectedLeadIdsRef.current = null;
            } else if (filtersChanged) {
              setSelectedLeadIds(new Set(leadList.map((l: { _id: string }) => l._id)));
            } else {
              setSelectedLeadIds((prev) => {
                const validIds = new Set(leadList.map((l: { _id: string }) => l._id));
                return new Set([...prev].filter((id) => validIds.has(id)));
              });
            }
          } else {
            setSelectedLeadIds(new Set());
          }
        } else {
          setRecipientCount(0);
          setLeads([]);
          setSelectedLeadIds(new Set());
        }
      } catch (error) {
        console.error("Error fetching recipients:", error);
        setRecipientCount(0);
        setLeads([]);
        setSelectedLeadIds(new Set());
      } finally {
        setIsLoadingRecipients(false);
      }
    };

    void fetchRecipientCount();
  }, [filters, filtersOperator, step, recipientQueryReady]);

  useEffect(() => {
    if (id) {
      setIsLoading(true);
      setRecipientQueryReady(false);
      fetch(`/api/email-campaigns/${id}`)
        .then((res) => res.json())
        .then((data) => {
          if (data.campaign) {
            const loadedSubject = data.campaign.subject || "";
            const loadedHtml =
              data.campaign.html || DEFAULT_EMAIL_CAMPAIGN_HTML;
            setSubject(loadedSubject);
            htmlRef.current = loadedHtml;
            setHtml(loadedHtml);

            if (
              data.campaign.filters &&
              Array.isArray(data.campaign.filters) &&
              data.campaign.filters.length > 0
            ) {
              setFilters(
                data.campaign.filters.map(
                  (f: Record<string, unknown>, i: number) =>
                    apiFilterToFilterItem(f, `loaded-${i}-${crypto.randomUUID()}`)
                )
              );
            }
            if (data.campaign.filtersOperator === "or") {
              setFiltersOperator("or");
            }
            if (data.campaign.selectedLeadIds?.length) {
              loadedSelectedLeadIdsRef.current = data.campaign.selectedLeadIds.map(
                (x: { toString: () => string }) => x.toString()
              );
            }
          }
        })
        .catch((error) => {
          console.error("Error loading campaign:", error);
        })
        .finally(() => {
          setIsLoading(false);
          setRecipientQueryReady(true);
        });
    }
  }, [id]);

  useEffect(() => {
    if (subjectRef.current && subject !== subjectRef.current.textContent) {
      subjectRef.current.textContent = subject;
    }
  }, [subject]);

  const handleSubjectChange = () => {
    if (subjectRef.current) {
      const newSubject = subjectRef.current.textContent || "";
      setSubject(newSubject);
    }
  };

  const handleHtmlChange = useCallback((value: string) => {
    htmlRef.current = value;
    setHtml(value);
  }, []);

  const handleCodeLiveChange = useCallback((value: string) => {
    htmlRef.current = value;
  }, []);

  const handleContentTabChange = useCallback(async (value: string) => {
    setContentTab(value);
    if (value !== "code") return;

    const currentHtml = htmlRef.current;
    if (!currentHtml.trim()) return;

    try {
      const formatted = await prettier.format(currentHtml, {
        parser: "html",
        plugins: [prettierPluginHtml],
        tabWidth: 2,
      });
      if (formatted !== htmlRef.current) {
        htmlRef.current = formatted;
        setHtml(formatted);
      }
    } catch {
      // Keep unformatted HTML if Prettier fails
    }
  }, []);

  const saveCampaign = async ({
    subject: subjectToSave,
    html: htmlToSave,
    filters: filtersToSave,
    filtersOperator: filtersOperatorToSave,
    selectedLeadIds: selectedIdsToSave,
  }: {
    subject?: string;
    html?: string;
    filters?: FilterItem[];
    filtersOperator?: FiltersOperator;
    selectedLeadIds?: Set<string>;
  } = {}): Promise<boolean> => {
    if (!id) return false;

    const filtersArray = (filtersToSave ?? filters)
      .map(filterItemToApiFilter)
      .filter((f): f is Record<string, unknown> => f != null);

    const selectedIds = selectedIdsToSave ?? selectedLeadIds;
    const selectedIdsPayload =
      selectedIds.size === 0
        ? []
        : selectedIds.size === leads.length
          ? null
          : Array.from(selectedIds);

    try {
      const response = await fetch(`/api/email-campaigns/${id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          subject: subjectToSave !== undefined ? subjectToSave : subject,
          html: htmlToSave !== undefined ? htmlToSave : htmlRef.current,
          filters: filtersArray.length > 0 ? filtersArray : undefined,
          filtersOperator: filtersOperatorToSave ?? filtersOperator,
          selectedLeadIds: selectedIdsPayload,
        }),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(
          (error as { error?: string }).error || "Failed to save campaign"
        );
      }

      return true;
    } catch (error) {
      console.error("Error saving campaign:", error);
      alert(
        error instanceof Error ? error.message : "Failed to save campaign"
      );
      return false;
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await saveCampaign({
        subject,
        html: htmlRef.current,
        filters,
        selectedLeadIds,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleNextStep = async () => {
    setIsSaving(true);
    try {
      const saved = await saveCampaign({
        subject,
        html: htmlRef.current,
        filters,
        selectedLeadIds,
      });
      if (!saved) return;

      if (step === 1) {
        setStep(2);
      } else if (step === 2) {
        setStep(3);
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handlePreviousStep = () => {
    setStep((current) => (current > 1 ? ((current - 1) as ComposeStep) : current));
  };

  const handleGoToStep = (targetStep: ComposeStep) => {
    if (targetStep < step) {
      setStep(targetStep);
    }
  };

  const handleBack = () => {
    navigate("/messages/emails");
  };

  const handleSendTest = () => {
    setIsDialogOpen(true);
  };

  const handleSendTestEmail = async () => {
    if (!testEmail.trim()) {
      return;
    }

    setIsSending(true);
    try {
      const response = await fetch("/api/email-campaigns/send-test", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email: testEmail.trim(),
          subject,
          html: htmlRef.current || "<div></div>",
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Failed to send test email");
      }

      setIsDialogOpen(false);
      setTestEmail("");
    } catch (error) {
      console.error("Error sending test email:", error);
      alert(
        error instanceof Error ? error.message : "Failed to send test email"
      );
    } finally {
      setIsSending(false);
    }
  };

  const handleSend = async () => {
    if (!subject.trim() || !id) return;
    if (leads.length > 0 && selectedLeadIds.size === 0) return;
    if (selectedRecipients.length === 0) return;

    setIsSaving(true);
    const saved = await saveCampaign({
      subject,
      html: htmlRef.current,
      filters,
      selectedLeadIds,
    });
    setIsSaving(false);
    if (!saved) return;

    setIsSending(true);
    try {
      const sendAt =
        sendAtLocal.trim() === ""
          ? null
          : new Date(sendAtLocal).toISOString();
      const response = await fetch("/api/email-campaigns/send", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          campaignId: id,
          ...(sendAt ? { sendAt } : {}),
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Failed to send email campaign");
      }

      setIsConfirmSendOpen(false);
      navigate("/messages/emails");
    } catch (error) {
      console.error("Error sending email campaign:", error);
      alert(
        error instanceof Error ? error.message : "Failed to send email campaign"
      );
    } finally {
      setIsSending(false);
    }
  };

  const recipientsSummary = recipientSummaryText({
    isLoadingRecipients,
    recipientCount,
    leads,
    selectedLeadIds,
  });

  const canProceedFromRecipients =
    !isLoadingRecipients &&
    !(leads.length > 0 && selectedLeadIds.size === 0);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <Loader2 className="h-12 w-12 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="w-full h-screen flex flex-col">
      <div className="border-b border-gray-200 px-6 py-4 flex items-center gap-4 shrink-0">
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={handleBack}
          className="h-8 w-8"
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
        {step === 1 ? (
          <div
            ref={subjectRef}
            contentEditable
            onInput={handleSubjectChange}
            className="flex-1 outline-none text-lg font-medium min-w-0 empty:before:content-[attr(data-placeholder)] empty:before:text-gray-400"
            style={{ minHeight: "1.5rem" }}
            data-placeholder="Subject"
          />
        ) : (
          <h1 className="flex-1 text-lg font-medium min-w-0">
            {step === 2 ? "Recipients" : "Review"}
          </h1>
        )}
        {step === 1 && (
          <Button
            type="button"
            variant="outline"
            onClick={handleSendTest}
            disabled={!subject.trim()}
          >
            Send a test
          </Button>
        )}
      </div>

      <div className="border-b px-6 py-3 shrink-0">
        <div className="flex items-center justify-center gap-0">
          {STEPS.map((s, index) => (
            <div key={s.number} className="flex items-center">
              {index > 0 && (
                <div className="h-px w-10 shrink-0 mx-2 bg-border" />
              )}
              <button
                type="button"
                onClick={() => handleGoToStep(s.number)}
                disabled={s.number > step || isSaving || isSending}
                className="flex items-center gap-2 disabled:cursor-not-allowed"
              >
                <span
                  className={cn(
                    "flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-medium transition-colors",
                    s.number === step
                      ? "bg-primary text-primary-foreground"
                      : s.number < step
                        ? "border border-primary text-foreground"
                        : "border border-muted-foreground/30 bg-muted text-muted-foreground"
                  )}
                >
                  {s.number}
                </span>
                <span
                  className={cn(
                    "text-sm font-medium hidden sm:inline",
                    s.number === step
                      ? "text-foreground"
                      : "text-muted-foreground"
                  )}
                >
                  {s.label}
                </span>
              </button>
            </div>
          ))}
        </div>
      </div>

      <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
        {step === 1 && (
          <div className="flex-1 min-h-0 overflow-hidden flex min-w-0">
            <Tabs
              value={contentTab}
              onValueChange={handleContentTabChange}
              className="flex-1 flex flex-col min-h-0 overflow-hidden min-w-0"
            >
              <div className="border-b px-4 py-2 shrink-0">
                <TabsList>
                  <TabsTrigger value="editor">Editor</TabsTrigger>
                  <TabsTrigger value="code">Code</TabsTrigger>
                  <TabsTrigger value="preview">Preview</TabsTrigger>
                </TabsList>
              </div>
              <TabsContent
                value="editor"
                forceMount
                className="flex-1 min-h-0 overflow-hidden mt-0 data-[state=inactive]:hidden"
              >
                <EmailLexicalEditor
                  html={html || DEFAULT_EMAIL_CAMPAIGN_HTML}
                  onChange={handleHtmlChange}
                  syncExternalHtml={contentTab === "editor"}
                />
              </TabsContent>
              <TabsContent
                value="code"
                className="flex-1 min-h-0 overflow-hidden mt-0 data-[state=inactive]:hidden"
              >
                <EmailCodeEditor
                  html={html}
                  onLiveChange={handleCodeLiveChange}
                  onCommit={handleHtmlChange}
                />
              </TabsContent>
              <TabsContent
                value="preview"
                className="flex-1 min-h-0 overflow-hidden mt-0 data-[state=inactive]:hidden"
              >
                <iframe
                  title="Email preview"
                  srcDoc={html || "<div></div>"}
                  className="w-full h-full border-0 bg-white"
                  sandbox=""
                />
              </TabsContent>
            </Tabs>
            <PageHtmlChatPanel
              html={html}
              onHtmlChange={handleHtmlChange}
              onSave={(savedHtml) => {
                void saveCampaign({ html: savedHtml });
              }}
            />
          </div>
        )}

        {step === 2 && (
          <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
            <div className="flex-1 overflow-y-auto p-6 max-w-3xl mx-auto w-full">
              <RecipientsFilter
                filters={filters}
                onFiltersChange={setFilters}
                filtersOperator={filtersOperator}
                onFiltersOperatorChange={setFiltersOperator}
                pages={pages}
                groups={groups}
                products={products}
                leads={leads}
                selectedLeadIds={selectedLeadIds}
                onSelectLead={handleSelectLead}
                onSelectAll={handleSelectAll}
                contactField="email"
              />
            </div>
            <div className="border-t px-6 py-3 shrink-0">
              <p className="text-sm text-muted-foreground">
                {isLoadingRecipients ? (
                  <>
                    <Loader2 className="h-4 w-4 inline mr-2 animate-spin" />
                    Calculating recipients...
                  </>
                ) : (
                  recipientsSummary
                )}
              </p>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="flex-1 overflow-y-auto p-6">
            <div className="max-w-3xl mx-auto w-full space-y-8">
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <h2 className="text-sm font-medium text-muted-foreground">
                    Subject
                  </h2>
                  {previewData?.subject && previewData.subject !== subject && (
                    <Badge variant="secondary" className="text-xs font-normal">
                      Personalized preview
                    </Badge>
                  )}
                </div>
                <p className="text-lg font-medium">
                  {(previewData?.subject || subject).trim() || (
                    <span className="text-muted-foreground italic">
                      No subject
                    </span>
                  )}
                </p>
              </div>

              <div className="space-y-3">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <h2 className="text-sm font-medium text-muted-foreground">
                    Email preview
                  </h2>

                  {selectedRecipients.length > 0 && (
                    <div className="flex items-center gap-2 flex-wrap">
                      <Popover
                        open={isRecipientPopoverOpen}
                        onOpenChange={setIsRecipientPopoverOpen}
                      >
                        <PopoverTrigger asChild>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="h-8 max-w-[280px] sm:max-w-[320px] justify-between text-xs font-normal truncate"
                          >
                            <span className="flex items-center gap-1.5 truncate">
                              <User className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                              <span className="truncate">
                                {currentPreviewLead
                                  ? [currentPreviewLead.firstName, currentPreviewLead.lastName]
                                      .filter(Boolean)
                                      .join(" ") || currentPreviewLead.email
                                  : "Select recipient"}
                              </span>
                            </span>
                            <ChevronsUpDown className="h-3.5 w-3.5 ml-1.5 opacity-50 shrink-0" />
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-80 p-0" align="end">
                          <div className="p-2 border-b">
                            <div className="relative">
                              <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-muted-foreground" />
                              <Input
                                placeholder="Search recipients..."
                                value={recipientSearchQuery}
                                onChange={(e) => setRecipientSearchQuery(e.target.value)}
                                className="h-8 pl-8 text-xs"
                              />
                            </div>
                          </div>
                          <div className="max-h-60 overflow-y-auto divide-y divide-border/40">
                            {filteredPreviewRecipients.length === 0 ? (
                              <div className="py-6 text-center text-xs text-muted-foreground">
                                No matching recipients
                              </div>
                            ) : (
                              filteredPreviewRecipients.map((lead) => {
                                const fullName = [lead.firstName, lead.lastName]
                                  .filter(Boolean)
                                  .join(" ");
                                const isSelected = lead._id === previewRecipientId;
                                return (
                                  <button
                                    key={lead._id}
                                    type="button"
                                    onClick={() => {
                                      setPreviewRecipientId(lead._id);
                                      setIsRecipientPopoverOpen(false);
                                    }}
                                    className={cn(
                                      "w-full text-left px-3 py-2 flex items-center justify-between text-xs hover:bg-muted/50 transition-colors",
                                      isSelected && "bg-muted font-medium"
                                    )}
                                  >
                                    <div className="min-w-0 flex-1 pr-2">
                                      <p className="truncate text-foreground">
                                        {fullName || "Unnamed Contact"}
                                      </p>
                                      {lead.email && (
                                        <p className="truncate text-[11px] text-muted-foreground">
                                          {lead.email}
                                        </p>
                                      )}
                                    </div>
                                    {isSelected && (
                                      <Check className="h-3.5 w-3.5 text-primary shrink-0" />
                                    )}
                                  </button>
                                );
                              })
                            )}
                          </div>
                        </PopoverContent>
                      </Popover>

                      <div className="flex items-center gap-1">
                        <Button
                          type="button"
                          variant="outline"
                          size="icon"
                          className="h-8 w-8"
                          onClick={handlePrevRecipient}
                          disabled={currentRecipientIndex <= 0 || isPreviewLoading}
                          title="Previous recipient"
                        >
                          <ChevronLeft className="h-4 w-4" />
                        </Button>
                        <span className="text-xs text-muted-foreground px-1.5 whitespace-nowrap">
                          {currentRecipientIndex >= 0 ? currentRecipientIndex + 1 : 0} of{" "}
                          {selectedRecipients.length}
                        </span>
                        <Button
                          type="button"
                          variant="outline"
                          size="icon"
                          className="h-8 w-8"
                          onClick={handleNextRecipient}
                          disabled={
                            currentRecipientIndex >= selectedRecipients.length - 1 ||
                            isPreviewLoading
                          }
                          title="Next recipient"
                        >
                          <ChevronRight className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  )}
                </div>

                {selectedRecipients.length === 0 ? (
                  <div className="rounded-md border border-dashed p-8 text-center bg-muted/20">
                    <p className="text-sm font-medium text-foreground">
                      No recipients selected for preview
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Go back to Step 2 (Recipients) to configure filters and select recipients.
                    </p>
                  </div>
                ) : (
                  <div className="relative border rounded-md overflow-hidden bg-white h-[min(560px,55vh)]">
                    {isPreviewLoading && (
                      <div className="absolute inset-0 bg-white/70 backdrop-blur-[1px] flex items-center justify-center z-10">
                        <div className="flex items-center gap-2 bg-white px-3 py-2 rounded-md shadow-sm border text-xs text-muted-foreground">
                          <Loader2 className="h-4 w-4 animate-spin text-primary" />
                          <span>Rendering preview...</span>
                        </div>
                      </div>
                    )}
                    {previewError && (
                      <div className="absolute top-2 left-2 right-2 z-20 bg-destructive/10 border border-destructive/20 text-destructive text-xs px-3 py-2 rounded">
                        {previewError}
                      </div>
                    )}
                    <iframe
                      title="Email review preview"
                      srcDoc={previewData?.html ?? html ?? "<div></div>"}
                      className="w-full h-full border-0"
                      sandbox=""
                    />
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <h2 className="text-sm font-medium text-muted-foreground">
                  Recipients
                </h2>
                <p className="text-base">
                  {isLoadingRecipients ? (
                    <span className="inline-flex items-center gap-2 text-muted-foreground">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Calculating recipients...
                    </span>
                  ) : (
                    recipientsSummary
                  )}
                </p>
                {filters.length > 0 && (
                  <p className="text-sm text-muted-foreground">
                    {filters.length} filter{filters.length !== 1 ? "s" : ""}{" "}
                    applied
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="send-at">Send At</Label>
                <Input
                  id="send-at"
                  type="datetime-local"
                  value={sendAtLocal}
                  onChange={(e) => setSendAtLocal(e.target.value)}
                />
                <p className="text-sm text-muted-foreground">
                  Optional. Leave empty to send as soon as possible.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="border-t px-6 py-4 flex items-center justify-between gap-4 shrink-0">
        <div>
          {step > 1 && (
            <Button
              type="button"
              variant="outline"
              onClick={handlePreviousStep}
              disabled={isSaving || isSending}
            >
              Back
            </Button>
          )}
        </div>
        <div className="flex items-center gap-3">
          <Button
            type="button"
            variant="outline"
            onClick={handleSave}
            disabled={isSaving || isSending}
          >
            {isSaving ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              "Save"
            )}
          </Button>
          {step < 3 ? (
            <Button
              type="button"
              onClick={handleNextStep}
              disabled={
                isSaving ||
                isSending ||
                (step === 2 && !canProceedFromRecipients)
              }
            >
              {isSaving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                "Continue"
              )}
            </Button>
          ) : (
            <Button
              type="button"
              onClick={() => setIsConfirmSendOpen(true)}
              disabled={
                !subject.trim() ||
                (leads.length > 0 && selectedLeadIds.size === 0) ||
                selectedRecipients.length === 0 ||
                isSending ||
                isSaving
              }
            >
              {isSending
                ? sendAtLocal.trim() !== ""
                  ? "Scheduling..."
                  : "Sending..."
                : sendAtLocal.trim() !== ""
                  ? "Schedule"
                  : "Send"}
            </Button>
          )}
        </div>
      </div>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Send Test Email</DialogTitle>
            <DialogDescription>
              Enter an email address to send a test of this campaign.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="email">Email Address</Label>
              <Input
                id="email"
                type="email"
                placeholder="test@example.com"
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !isSending) {
                    handleSendTestEmail();
                  }
                }}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setIsDialogOpen(false)}
              disabled={isSending}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleSendTestEmail}
              disabled={isSending || !testEmail.trim()}
            >
              {isSending ? "Sending..." : "Send Test"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={isConfirmSendOpen}
        onOpenChange={(open) => {
          if (!isSending && !isSaving) {
            setIsConfirmSendOpen(open);
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {sendAtLocal.trim() !== "" ? "Schedule Email Campaign" : "Send Email Campaign"}
            </DialogTitle>
            <DialogDescription>
              {sendAtLocal.trim() !== ""
                ? "Are you sure you want to schedule this email campaign? This will send "
                : "Are you sure you want to send this email campaign? This will send "}
              <span className="font-semibold text-foreground">
                {selectedRecipients.length === 1
                  ? "1 email"
                  : `${selectedRecipients.length.toLocaleString()} emails`}
              </span>
              {sendAtLocal.trim() !== "" ? " at the selected time." : "."}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setIsConfirmSendOpen(false)}
              disabled={isSending || isSaving}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleSend}
              disabled={isSending || isSaving}
            >
              {isSending || isSaving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  {isSending
                    ? sendAtLocal.trim() !== ""
                      ? "Scheduling..."
                      : "Sending..."
                    : "Saving..."}
                </>
              ) : sendAtLocal.trim() !== "" ? (
                "Schedule"
              ) : (
                "Send"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
